import type { Location } from 'history';
import { useEffect } from 'react';

import AuthenticationStore from '~/auth/authenticationStore';
import { trackSegment } from '~/components/SegmentAnalytics';
import { LoginViewType } from '~/enums/LoginViewType';
import { QueryParamType } from '~/enums/QueryParamType';
import { getClientFromUrl } from '~/routing/utils';
import Configuration from '~/services/configuration';
import { RedirectState } from '~/types/Router';
import { getPageSearchQuery } from '~/utils/searchQuery';

import { handlePopupLoginSuccess } from './popupLoginUtils';

const AS = AuthenticationStore();

export const useRolodexLoginProps = ({
  setStage,
  location,
  navigate,
  workspace,
  enableOidc,
  isPopupMode = false,
}: {
  setStage: (stage: LoginViewType) => void;
  location: Location<RedirectState>;
  navigate: (path: string) => void;
  workspace: string;
  enableOidc: boolean;
  isPopupMode?: boolean;
}) => {
  const searchParamKey = [...getPageSearchQuery().keys()][0];

  // clean csrf token in auth store on login page load
  useEffect(() => AS.setCsrf(null), []);

  useEffect(() => {
    if (workspace && enableOidc) {
      AS.clearAuthentication();

      const subdomain = getClientFromUrl();
      const workspaceBase = `${Configuration.rolodexEndpoint}/api/v1/oidc/${workspace}`;

      // Preserve mode=popup in return URL so office.js is loaded after OIDC redirect
      const popupParam = isPopupMode ? '?mode=popup' : '';
      const returnUrl = `${window.location.origin}/${subdomain}`;

      if (location?.state?.from) {
        const { pathname, search, hash } = location.state.from;
        // If in popup mode, append mode=popup to existing search params
        const searchWithPopup = isPopupMode
          ? search
            ? `${search}&mode=popup`
            : '?mode=popup'
          : search;
        window.location.href = `${workspaceBase}?return_url=${encodeURIComponent(`${returnUrl}${pathname}${searchWithPopup}${hash}`)}`;
      } else {
        window.location.href = `${workspaceBase}?return_url=${encodeURIComponent(`${returnUrl}${popupParam}`)}`;
      }
    }
  }, [workspace, enableOidc, location, isPopupMode]);

  useEffect(
    () => {
      const handleRolodexResetPasswordCallback = (showWelcome = false) => {
        AS.removeInactiveTimer();
        AS.saveJwtTokenCreationTime();

        setLogIn(showWelcome);
      };

      const handleOidcCallback = () => {
        trackSegment('User SSO Login');
        AS.removeInactiveTimer();
        AS.saveJwtTokenCreationTime();
        setLogIn();
      };

      switch (searchParamKey) {
        case QueryParamType.SsoSuccess:
          return handleOidcCallback();
        case QueryParamType.RolodexFirstLoginSuccess:
          return handleRolodexResetPasswordCallback(true);
        case QueryParamType.RolodexResetSuccess:
          return handleRolodexResetPasswordCallback();
        default:
          return undefined;
      }
    },
    [location.search, navigate], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setLogIn = (showWelcome = false) => {
    setTimeout(() => {
      if (AS.isLoggedIn()) {
        // In popup mode, use Office Dialog API to send credentials back
        if (isPopupMode) {
          handlePopupLoginSuccess();
        } else {
          showWelcome
            ? setStage(LoginViewType.LoggedInWithWelcome)
            : setStage(LoginViewType.LoggedIn);
        }
      }
    }, 250);
  };

  const onRolodexLoginComplete = () => {
    setTimeout(() => {
      if (AS.isLoggedIn()) {
        AS.saveJwtTokenCreationTime();
        // In popup mode, use Office Dialog API to send credentials back
        if (isPopupMode) {
          handlePopupLoginSuccess();
        } else {
          setStage(LoginViewType.LoggedIn);
        }
      }
    }, 100);
  };

  return { onRolodexLoginComplete };
};
